import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useRealtimeFrame, useRealtimeStatus } from '../../shared/api/realtime';
import { bulkKeys, bulkOperation, isFinished, type TBulkOperation } from './api';

const STATES: readonly TBulkOperation['state'][] = [
  'pending',
  'running',
  'paused',
  'done',
  'failed',
  'cancelled',
];

export const useBulkOperation = (id: string | null) => {
  const client = useQueryClient();
  const status = useRealtimeStatus();
  const query = useQuery({
    queryKey: bulkKeys.one(id ?? ''),
    queryFn: () => bulkOperation(id as string),
    enabled: id !== null,
  });

  useRealtimeFrame('bulk_operation.updated', (frame) => {
    if (frame.operation.id !== id) return;
    const next = frame.operation;
    client.setQueryData<TBulkOperation>(bulkKeys.one(id), (current) =>
      current
        ? {
            ...current,
            state: STATES.find((state) => state === next.state) ?? current.state,
            total: next.total,
            processed: next.processed,
            succeeded: next.succeeded,
            failed: next.failed,
            current: next.current,
            etaMs: next.etaMs,
          }
        : current,
    );
    if (['done', 'failed', 'cancelled'].includes(next.state)) {
      void client.invalidateQueries({ queryKey: bulkKeys.one(id) });
    }
  });

  // Frames missed while the socket was down are not replayed for jobs: re-read on return.
  useEffect(() => {
    if (status === 'live' && id !== null) {
      void client.invalidateQueries({ queryKey: bulkKeys.one(id) });
    }
  }, [status, id, client]);

  const operation = query.data;
  return { query, operation, finished: operation ? isFinished(operation) : false };
};
